// src/services/npiRegistryDemo.ts
// ============================================================
// DEMO NPI REGISTRY LOOKUP — static, offline, NPPES-shaped
//
// Mimics the shape of an NPPES organisational-provider resolve: you supply an NPI number and
// the registry returns the organisation name. In the product this would call NPPES (or the
// backend's cached mirror); here it resolves from a static table so the demo works with no
// backend and no network.
//
// WHY THE NAME IS NOT USER-EDITABLE: an organisation name is a PROPERTY of the NPI in the
// national registry, not a free-text field. Letting a user type a name next to a number
// invites a mismatch that looks authoritative on a board packet. The number is the input;
// the name is resolved and rendered read-only.
//
// ⚠️  THE SEEDED NPIs ARE DELIBERATELY CHECKSUM-INVALID.
// All three fail the standard NPI check-digit algorithm (Luhn over '80840' + the first 9
// digits). That is intentional and load-bearing for a public demo: an invalid NPI cannot
// collide with a real provider's identifier. DO NOT "fix" them into valid numbers — any valid
// 10-digit NPI belongs to a real organisation. Verified invalid at authoring time; re-verify
// with npiCheckDigitIsValid() below if they are ever changed.
// ============================================================

export interface NpiRegistryRecord {
  npi: string;
  organizationName: string;
  /** NPPES enumeration type — organisations are Type 2. Cosmetic here. */
  enumerationType: 'NPI-2';
  primaryTaxonomy: string;
  city: string;
  state: string;
}

/** The demo registry. Keyed by NPI number. */
const DEMO_REGISTRY: Record<string, NpiRegistryRecord> = {
  '1000000010': {
    npi: '1000000010',
    organizationName: 'ACME Health System',
    enumerationType: 'NPI-2',
    primaryTaxonomy: 'General Acute Care Hospital',
    city: 'Charlotte',
    state: 'NC',
  },
  '1000000028': {
    npi: '1000000028',
    organizationName: 'ACME Medical Group',
    enumerationType: 'NPI-2',
    primaryTaxonomy: 'Multi-Specialty Group',
    city: 'Charlotte',
    state: 'NC',
  },
  '1000000036': {
    npi: '1000000036',
    organizationName: 'ACME Community Hospital',
    enumerationType: 'NPI-2',
    primaryTaxonomy: 'General Acute Care Hospital',
    city: 'Winston-Salem',
    state: 'NC',
  },
};

export type NpiResolveStatus = 'idle' | 'resolving' | 'resolved' | 'not_found' | 'invalid';

export interface NpiResolveResult {
  status: NpiResolveStatus;
  record?: NpiRegistryRecord;
  /** Short human-readable note for the UI. */
  message?: string;
}

/** An NPI is 10 digits. Shape check only — NOT the check digit (see the header). */
export function npiHasValidShape(npi: string): boolean {
  return /^\d{10}$/.test((npi || '').trim());
}

/**
 * The standard NPI check-digit algorithm (Luhn over '80840' + first 9 digits).
 * Exported for verification only — the demo lookup does NOT gate on it, precisely because
 * the seeded demo NPIs are intentionally invalid.
 */
export function npiCheckDigitIsValid(npi: string): boolean {
  if (!npiHasValidShape(npi)) return false;
  const digits = `80840${npi.slice(0, 9)}`;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    // double every second digit counting from the right
    const fromRight = digits.length - 1 - i;
    let d = Number(digits[i]);
    if (fromRight % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return (10 - (sum % 10)) % 10 === Number(npi[9]);
}

/** Synchronous resolve — the table lookup with no latency. */
export function resolveNpiSync(npi: string): NpiResolveResult {
  const key = (npi || '').trim();
  if (!key) return { status: 'idle' };
  if (!npiHasValidShape(key)) {
    return { status: 'invalid', message: 'An NPI is 10 digits.' };
  }
  const record = DEMO_REGISTRY[key];
  return record
    ? { status: 'resolved', record }
    : { status: 'not_found', message: 'Not found in registry' };
}

/**
 * Async resolve with a short, deterministic delay so the UI can show a "Resolving…" state —
 * the way a real NPPES round-trip would feel. Deterministic (no Math.random) so demo timing
 * is repeatable.
 */
export function resolveNpi(npi: string, delayMs = 260): Promise<NpiResolveResult> {
  const result = resolveNpiSync(npi);
  if (result.status === 'idle' || result.status === 'invalid') {
    return Promise.resolve(result);
  }
  return new Promise((resolve) => {
    setTimeout(() => resolve(result), delayMs);
  });
}

/** Every NPI the demo registry knows — handy for seeding and tests. */
export function knownDemoNpis(): string[] {
  return Object.keys(DEMO_REGISTRY);
}

export default resolveNpi;
